import React, { useState } from 'react';
import { Card, Col, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import CardHeader from '../../components/CardHeader';
import { DropdownActionItem } from '../../../../atomic/molecules/DropdownActionItem';
import {
  AbsenceApprovalStatus,
  MyAbsencesQuery,
  useDeleteUpcomingAbsenceMutation,
} from '../../__generated__/graphql';

import AbsenceAmount from './components/AbsenceAmount';
import AbsenceDateTime from './components/AbsenceDateTime';
import AbsenceStatus from './components/AbsenceStatus';
import { useMyAbsencesRefetchOption } from './useMyAbsencesRefetchOption';
import AbsenceIcon from './components/AbsenceIcon';
import CommentsPopover from '../../components/Comment/CommentsPopover';
import { RelatedObjectNames } from '../../components/Comment/types';
import { DropdownActions } from '../../../../atomic/templates/DropdownActions';
import { DeleteAbsenceDisabledDetails } from './MyAbsences/types';
import { Maybe } from 'graphql/jsutils/Maybe';
import { mapUpcomingAbsences } from './mappings';
import { isMultiDayAndHourly } from './components/MultiDayAbsenceDetails/logic';
import MultiDayAbsenceBtn from './components/MultiDayAbsenceDetails/MultiDayAbsenceBtn';
import MultiDayAbsenceDetails from './components/MultiDayAbsenceDetails';
import EditAbsenceSidebar from '../../components/EditAbsenceSidebar';

type UpdatedAbsence = Absence & DeleteAbsenceDisabledDetails;

type Absences = MyAbsencesQuery['me']['upcomingAbsences'];
type Absence = Absences[0];

type Props = {
  absences: Absences | undefined;
};

const Loading: React.FC<React.PropsWithChildren<unknown>> = () => (
  <div className="text-center">
    <div className="spinner-border spinner-border-sm" role="status"></div>
  </div>
);

const canDeleteAbsence = (absence: Absence) =>
  (absence.flair__Category_Restrict_Absence_Deletion__c &&
    absence.flair__Approval_Status__c !== AbsenceApprovalStatus.Approved) ||
  !absence.flair__Category_Restrict_Absence_Deletion__c;

// Editing exists so an employee can still attach the document, so it stays
// available for as long as one can be attached.
const canEditAbsence = (absence: Absence) => {
  if (absence.flair__Requires_Approval__c) return false;

  const policy =
    absence.employeeAbsenceCategory?.absenceRequestPolicies[0]
      ?.absenceRequestPolicy;

  return (
    absence.category.flair__Allow_Document_Upload__c ||
    (!!policy?.flair__Document_Upload__c &&
      (policy.flair__Min_Amount_For_Document_Upload__c ?? 0) > 0)
  );
};

export const UpcomingAbsencesCard: React.FC<React.PropsWithChildren<Props>> = ({
  absences = [],
}) => {
  const [deletingAbsence, setDeletingAbsence] = useState<string>('');
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const myAbsencesRefetchQuery = useMyAbsencesRefetchOption();
  const [deleteUpcomingAbsence, { loading }] = useDeleteUpcomingAbsenceMutation(
    {
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [myAbsencesRefetchQuery],
    },
  );

  const [
    selectedAbsenceIdForMultiDayAbsenceDetails,
    setSelectedAbsenceIdForMultiDayAbsenceDetails,
  ] = useState<Maybe<string>>();

  const [selectedAbsenceIdForEdit, setSelectedAbsenceIdForEdit] =
    useState<Maybe<string>>();

  const handleCloseMultiDayDetailsSidebar = () => {
    setSelectedAbsenceIdForMultiDayAbsenceDetails(null);
  };

  const handleCloseEditSidebar = () => {
    setSelectedAbsenceIdForEdit(null);
  };

  const errorHandler = useMutationErrorHandler(() =>
    t('absences.cards.upcomingAbsences.delete.failure'),
  );

  const isDeleting = (id: string): boolean => loading && deletingAbsence === id;

  const hasAbsences = absences && absences.length > 0;

  const updatedAbsences: UpdatedAbsence[] = mapUpcomingAbsences(absences);

  const isDeleteDisabled = (absence: UpdatedAbsence) => {
    return loading || !!absence.deleteDisabled;
  };

  const handleDelete = (id: string) => {
    setDeletingAbsence(id);
    deleteUpcomingAbsence({
      variables: { id },
    })
      .then(() =>
        addSuccess(t('absences.cards.upcomingAbsences.delete.success')),
      )
      .catch(errorHandler)
      .finally(() => setDeletingAbsence(''));
  };

  return (
    <Card>
      <CardHeader>{t('absences.cards.upcomingAbsences.title')}</CardHeader>
      <Card.Body>
        <ListGroup variant="flush" className="my-n4">
          {hasAbsences ? (
            updatedAbsences.map((absence) => {
              const rejectedRequest = absence.approvalRequests?.find(
                (req) => req.flair__Approval_Status__c === 'REJECTED',
              );
              const rejectionComment =
                rejectedRequest?.flair__Approver_Comment__c ?? null;

              return (
                <ListGroup.Item key={absence.Id}>
                  <Row className="align-items-center">
                    <Col className="col-auto">
                      <AbsenceIcon
                        icon={absence.flair__Category_Icon__c}
                        color={absence.flair__Category_Color__c}
                      />
                    </Col>
                    <Col>
                      <h4>
                        <AbsenceDateTime
                          date={absence.flair__Start_Date__c}
                          time={absence.flair__Start_Time__c}
                          type={absence.flair__Type__c}
                          halfDay={absence.flair__Start_Date_Half_Day__c}
                        />
                        &nbsp;-&nbsp;
                        <AbsenceDateTime
                          date={absence.flair__End_Date__c}
                          time={absence.flair__End_Time__c}
                          type={absence.flair__Type__c}
                          halfDay={absence.flair__End_Date_Half_Day__c}
                        />
                      </h4>

                      <div className="card-text small text-dark mb-1 d-flex">
                        <AbsenceStatus
                          status={absence.flair__Approval_Status__c}
                        />
                        {rejectionComment && (
                          <div className="bg-secondary-soft border my-1 py-1 px-3 rounded-1">
                            {rejectionComment}
                          </div>
                        )}
                        &nbsp;&#xB7;&nbsp;
                        {absence.flair__Category_Name__c}&nbsp;&#xB7;&nbsp;
                        <AbsenceAmount
                          type={absence.flair__Type__c}
                          amount={absence.flair__Withdrawal_Amount__c}
                          variant="work"
                        />
                        &nbsp;&#xB7;&nbsp;
                        <AbsenceAmount
                          type={absence.flair__Type__c}
                          amount={absence.flair__Amount__c}
                          variant="calendar"
                        />
                        {isMultiDayAndHourly(absence) && (
                          <>
                            &nbsp;&#xB7;&nbsp;
                            <MultiDayAbsenceBtn
                              absenceId={absence.Id}
                              onClick={
                                setSelectedAbsenceIdForMultiDayAbsenceDetails
                              }
                            />
                          </>
                        )}
                      </div>
                    </Col>
                    <Col className="col-auto">
                      <CommentsPopover
                        recordId={absence.Id}
                        relatedObjectName={RelatedObjectNames.Absence}
                        commentsCount={absence.commentsCount}
                        mode="icon"
                      />
                    </Col>
                    {(canDeleteAbsence(absence) || canEditAbsence(absence)) && (
                      <Col className="col-auto">
                        {isDeleting(absence.Id) ? (
                          <Loading />
                        ) : (
                          <DropdownActions id={`${absence.Id}-actions`}>
                            {canDeleteAbsence(absence) && (
                              <DropdownActionItem
                                onClick={() => {
                                  handleDelete(absence.Id);
                                }}
                                disabled={isDeleteDisabled(absence)}
                                tooltip={absence.deleteDisabled?.reason}>
                                {t(
                                  'absences.cards.upcomingAbsences.actions.delete',
                                )}
                              </DropdownActionItem>
                            )}
                            {canEditAbsence(absence) && (
                              <DropdownActionItem
                                onClick={() => {
                                  setSelectedAbsenceIdForEdit(absence.Id);
                                }}
                                disabled={loading}>
                                {t(
                                  'absences.cards.upcomingAbsences.actions.edit',
                                )}
                              </DropdownActionItem>
                            )}
                          </DropdownActions>
                        )}
                      </Col>
                    )}
                  </Row>
                </ListGroup.Item>
              );
            })
          ) : (
            <ListGroup.Item>
              <Row className="align-items-center justify-content-center ">
                <Col className="col-auto">
                  <p className="text-justify mb-0">
                    {t('absences.cards.upcomingAbsences.emptyState')}
                  </p>
                </Col>
              </Row>
            </ListGroup.Item>
          )}
        </ListGroup>
        {selectedAbsenceIdForMultiDayAbsenceDetails && (
          <MultiDayAbsenceDetails
            absenceId={selectedAbsenceIdForMultiDayAbsenceDetails}
            show={true}
            onClose={handleCloseMultiDayDetailsSidebar}
          />
        )}
        {selectedAbsenceIdForEdit && (
          <EditAbsenceSidebar
            show={!!selectedAbsenceIdForEdit}
            onClose={handleCloseEditSidebar}
            absenceId={selectedAbsenceIdForEdit}
            absenceCategoryId={
              absences.find((a) => a.Id === selectedAbsenceIdForEdit)
                ?.flair__Employee_Absence_Category__c || ''
            }
            onSuccess={handleCloseEditSidebar}
            refetchQueries={[myAbsencesRefetchQuery]}
          />
        )}
      </Card.Body>
    </Card>
  );
};
